import { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const LEVEL_CLS = { INFO: "bg-[var(--line)] text-[var(--ink)]", WARNING: "bg-amber-200 text-amber-900", ERROR: "bg-red-700 text-white" };
const LEVELS = [["all", "All levels"], ["INFO,WARNING,ERROR", "Info and above"], ["WARNING,ERROR", "Warnings & errors"], ["ERROR", "Errors only"]];

const Row = ({ l, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--line)]" data-testid={`log-row-${i}`}>
      <button onClick={() => setOpen(!open)} className="w-full grid grid-cols-[150px_80px_100px_1fr] gap-4 py-3 px-4 text-left text-sm hover:bg-[var(--paper)] transition-colors min-w-[720px]">
        <span className="text-xs text-[var(--muted-ink)] tabular-nums">{new Date(l.ts).toLocaleString()}</span>
        <span><span className={`text-[10px] tracking-wider px-2 py-0.5 ${LEVEL_CLS[l.level] || LEVEL_CLS.INFO}`}>{l.level}</span></span>
        <span className="text-xs uppercase tracking-wider text-[var(--muted-ink)]">{l.category}</span>
        <span className="break-words">{l.message}</span>
      </button>
      {open && l.details && <pre className="text-xs bg-[var(--ink)] text-[var(--cream)] p-4 mx-4 mb-3 overflow-x-auto">{JSON.stringify(l.details, null, 2)}</pre>}
    </div>
  );
};

export default function Logs() {
  const [data, setData] = useState({ items: [], categories: [] });
  const [level, setLevel] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [live, setLive] = useState(true);

  const load = useCallback(() => {
    const params = { q, ...(level !== "all" && { level }), ...(category !== "all" && { category }) };
    api.get("/logs", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [level, category, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!live) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [live, load]);

  const clear = async () => { await api.delete("/logs"); toast.success("Logs cleared"); load(); };
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(data.items, null, 2)], { type: "application/json" }));
    Object.assign(document.createElement("a"), { href: url, download: `catalog-logs-${Date.now()}.json` }).click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 rise">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--muted-ink)] mb-3">Activity &amp; errors · kept 30 days</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Logs</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} data-testid="logs-refresh-button"><RefreshCw size={14} className="mr-2" />Refresh</Button>
          <Button variant="outline" onClick={download} data-testid="logs-download-button"><Download size={14} className="mr-2" />Export</Button>
          <Button variant="outline" onClick={clear} data-testid="logs-clear-button"><Trash2 size={14} className="mr-2" />Clear</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search messages…" className="max-w-xs bg-[var(--cream)]" data-testid="logs-search-input" />
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-48 bg-[var(--cream)]" data-testid="logs-level-select"><SelectValue /></SelectTrigger>
          <SelectContent>{LEVELS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44 bg-[var(--cream)]" data-testid="logs-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {data.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm ml-auto"><Switch checked={live} onCheckedChange={setLive} data-testid="logs-live-switch" />Live</label>
      </div>
      <div className="bg-[var(--cream)] page-shadow overflow-x-auto" data-testid="logs-table">
        {data.items.length === 0 ? <p className="p-10 text-center text-sm text-[var(--muted-ink)]" data-testid="logs-empty">No log entries</p>
          : data.items.map((l, i) => <Row key={`${l.ts}-${i}`} l={l} i={i} />)}
      </div>
    </div>
  );
}
